<template>
    <component :is="tag" v-tooltip="germanDate">{{parsedDate}}</component>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import dateHelper, {timestamp} from '../utils/portuna/dateHelper';

    @Component({
        name: 'RelativeDateTime',
    })
    export default class RelativeDateTime extends Vue
    {
        @Prop({
            type: String,
            default: 'div',
            required: false,
        }) tag: string;

        @Prop({
            type: [String, Number],
            required: true,
        }) date: string | number;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) daily: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) upperFirst: boolean;

        @Prop({
            type: String,
        }) tooltipFormat: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) showTime: string;

        now = Date.now();

        timeoutId: number = 0;

        forceDaily: boolean = false;

        @Watch('date')
        watchDate()
        {
            this.recalculate();
        }

        get current(): number
        {
            let date: boolean | string | number = this.now;

            // ensure there isn't a time @ daily check
            if (this.daily || this.forceDaily)
            {
                date = dateHelper('Y-m-d', date);
            }

            return timestamp(date);
        }

        get difference()
        {
            return this.current - this.timestamp;
        }

        get dayDifference()
        {
            this.forceDaily = true;
            let diff        = this.current - this.timestamp;
            this.forceDaily = false;

            return diff;
        }

        get germanDate()
        {
            return dateHelper(this.tooltipFormat || (this.daily ? 'd.m.Y' : 'd.m.Y H:i'), this.date);
        }

        get time()
        {
            return dateHelper('H:i', this.date);
        }

        get parsedDate()
        {
            let past = this.difference > 0;
            let term = past ? 'vor ' : 'in ';
            let text: string;

            let {seconds, minutes, hours, days, months, years} = this.calculateDifference();

            if (years > 1)
            {
                text = term + years + ' Jahren';
            }
            else if (years === 1)
            {
                text = term + 'einem Jahr';
            }
            else if (months > 1)
            {
                text = term + months + ' Monaten';
            }
            else if (months === 1)
            {
                text = term + 'einem Monat';
            }
            else if (days > 2)
            {
                text = term + days + ' Tagen';
            }
            else if (days === 2)
            {
                text = past ? 'Vorgestern' : 'Übermorgen';
            }
            else if (days === 1)
            {
                text = past ? 'Gestern' : 'Morgen';
            }
            else if (this.daily)
            {
                text = 'Heute';
            }
            else if (hours > 1)
            {
                text = term + hours + ' Stunden';
            }
            else if (hours === 1)
            {
                text = term + 'einer Stunde';
            }
            else if (minutes > 1)
            {
                text = term + minutes + ' Minuten';
            }
            else if (minutes === 1)
            {
                text = term + 'einer Minute';
            }
            else if (seconds > 5)
            {
                text = term + seconds + ' Sekunden';
            }
            else
            {
                text = 'gerade eben';
            }

            if(this.showTime && (this.daily || days > 0))
            {
                text += ', ' + this.time;
            }

            return this.upperFirst ? text.charAt(0).toUpperCase() + text.slice(1) : text;
        }

        get timestamp(): number
        {
            let date: boolean | string | number = this.date;

            // ensure there isn't a time @ daily check
            if (this.daily || this.forceDaily)
            {
                date = dateHelper('Y-m-d', date);
            }

            return timestamp(date);
        }

        created()
        {
            this.setTimeout(1);
        }

        beforeUnmount(): void
        {
            this.clearTimeout();
        }

        clearTimeout()
        {
            this.timeoutId && window.clearTimeout(this.timeoutId);
        }

        setTimeout(seconds: number): void
        {
            this.timeoutId = window.setTimeout(() =>
            {
                this.now = Date.now();

                let nextCalculate;

                if (this.daily)
                {
                    nextCalculate = 3600; // 60m
                }
                else
                {
                    let {minutes, hours} = this.calculateDifference();

                    if (hours >= 5)
                    {
                        nextCalculate = 3600; // 60m
                    }
                    if (hours >= 1)
                    {
                        nextCalculate = 300; // 5m
                    }
                    else if (minutes >= 1)
                    {
                        nextCalculate = 30; // 30s
                    }
                    else
                    {
                        nextCalculate = 1; // 1s
                    }
                }

                this.setTimeout(nextCalculate);

            }, seconds * 1000);
        }

        private recalculate()
        {
            this.clearTimeout();
            this.now = Date.now();
            this.setTimeout(1);
        }

        private calculateDifference()
        {
            let seconds = Math.abs(this.difference),
                minutes = seconds / 60,
                hours   = minutes / 60,
                days    = Math.round(Math.abs(this.dayDifference) / 60 / 60 / 24),
                months  = days / 30.25,
                years   = months / 12;

            return {
                seconds: Math.trunc(seconds),
                minutes: Math.trunc(minutes),
                hours: Math.trunc(hours),
                days: Math.trunc(days),
                months: Math.trunc(months),
                years: Math.trunc(years),
            };
        }
    };
</script>
